import React from 'react';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  AI_AGENT_COMPOSER_PLACEHOLDER,
  AI_AGENT_DISCLAIMER,
  AI_AGENT_TOKENS,
} from '../AIAgentConstants';

interface Props {
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  showDisclaimer: boolean;
}

export default function AIAgentComposer({
  draft,
  onDraftChange,
  onSend,
  disabled,
  showDisclaimer,
}: Props) {
  const canSend = !disabled && Boolean(draft.trim());

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) {
        onSend();
      }
    }
  };

  return (
    <Box
      sx={{
        px: 3,
        py: 3,
        borderTop: `1px solid ${AI_AGENT_TOKENS.blueGrey100}`,
        backgroundColor: AI_AGENT_TOKENS.white,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={AI_AGENT_COMPOSER_PLACEHOLDER}
          disabled={disabled}
          inputProps={{ 'aria-label': 'Campo de pergunta ao assistente' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  sx={{
                    backgroundColor: AI_AGENT_TOKENS.sendAdornmentBg,
                    borderRadius: `${AI_AGENT_TOKENS.borderRadius}px`,
                  }}
                >
                  <IconButton
                    aria-label="Enviar pergunta"
                    onClick={onSend}
                    disabled={!canSend}
                    size="small"
                    sx={{
                      p: 1,
                      color: canSend
                        ? AI_AGENT_TOKENS.primary
                        : 'rgba(0, 0, 0, 0.56)',
                    }}
                  >
                    <SendIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: AI_AGENT_TOKENS.surface,
            '& .MuiOutlinedInput-root': {
              borderRadius: `${AI_AGENT_TOKENS.borderRadius}px`,
              fontFamily: AI_AGENT_TOKENS.fontFamily,
              fontSize: 16,
              alignItems: 'center',
              pr: 0.5,
              '& fieldset': {
                borderColor: AI_AGENT_TOKENS.border,
              },
            },
          }}
        />

        {showDisclaimer && (
          <Typography
            sx={{
              fontFamily: AI_AGENT_TOKENS.fontFamily,
              fontWeight: 400,
              fontSize: 12,
              lineHeight: 1.43,
              letterSpacing: '0.17px',
              color: AI_AGENT_TOKENS.textDisabled,
              textAlign: 'center',
              width: '100%',
            }}
          >
            {AI_AGENT_DISCLAIMER}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
